import { ExternalLinkIcon } from 'lucide-react'

import { useGetCompany } from '@/api/company.hook'
import { CompanyLifecycle } from '@/gen/inventory/v1/company_lifecycle_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { buildThirdPartyUrl } from '@/lib/url/urls'

import { getInherentRiskTrackingValue, useAssessmentAnalytics } from '@/pages/assessment/analytics'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { StatisticsColumn } from '@/components/lemmala-design-system/statistics/stats-column'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { CompanyLifecycleBadge } from '@/components/shared/company-lifecycle-badge/company-lifecycle-badge'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'
import { getInherentRisk } from '@/components/shared/risk-badge/utils'

type RightSideCompanyInfoProps = {
  companyId: string
}

export const RightSideCompanyInfo = ({ companyId }: RightSideCompanyInfoProps) => {
  const trackingContext = useAssessmentAnalytics()
  const { data: company, isLoading: isLoadingCompany } = useGetCompany(companyId)
  const trackCompanyInfoViewMoreClick = useTrackCallback(
    'third-party.assessment.company-info.view-more.click',
  )

  if (isLoadingCompany) {
    return <Loading />
  }

  return (
    <div className='px-6'>
      <div className='truncate py-4 text-left text-main-foreground-primary text-label-default'>
        {company?.company?.profile?.name}
      </div>
      <StatisticsColumn
        items={[
          {
            title: 'Inherent risk',
            children: (
              <RiskBadge
                className='w-fit'
                riskLevel={getInherentRisk(
                  company?.company?.validatedRisk,
                  company?.company?.calculatedRisk,
                )}
              />
            ),
          },
          {
            title: 'Lifecycle',
            children: (
              <CompanyLifecycleBadge
                className='w-fit'
                lifecycle={company?.company?.lifecycle ?? CompanyLifecycle.UNSPECIFIED}
              />
            ),
          },
        ]}
      />

      <div className='mb-2 mt-4'>{company?.company?.profile?.longDescription || ''}</div>
      <Button
        variant='link'
        className='p-0'
        size='sm'
        onClick={() => {
          if (trackingContext) {
            trackCompanyInfoViewMoreClick({
              ...trackingContext,
              inherentRisk: getInherentRiskTrackingValue(
                getInherentRisk(company?.company?.validatedRisk, company?.company?.calculatedRisk),
              ),
            })
          }

          window.open(buildThirdPartyUrl(companyId), '_blank')
        }}
      >
        <TextWithIcon
          text='View more'
          icon={<ExternalLinkIcon className='size-3' />}
          iconPosition='end'
        />
      </Button>
    </div>
  )
}
